import { ChangeEvent, useEffect, useState } from 'react';
import { useClipboard } from '../hooks/useClipboard';
import { ComputeBudgetProgram, Connection, Keypair, PublicKey, TransactionInstruction, TransactionMessage, VersionedTransaction } from '@solana/web3.js';
import { useSplToken } from '../hooks/useSplToken';
import { useJito } from '../hooks/useJito';
import {
    ASSOCIATED_TOKEN_PROGRAM_ID,
    AuthorityType,
    TOKEN_PROGRAM_ID,
    createAssociatedTokenAccountInstruction,
    createSetAuthorityInstruction,
    createTransferInstruction,
    getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import axios from 'axios';
import FormData from 'form-data';
import bs58 from 'bs58';
import pvmb from '../utils/pvmb';
import { useBackend } from '../hooks/useBackend';
import { WSOL } from '@raydium-io/raydium-sdk';
import { createTokenJitoTip, createTokenPriorityFee } from '../../ConfigFees';
import { CreateTokenTransferToHolderDefaultPercentage } from '../../ConfigConstants';

export function CreateTokenForm({
    connection,
    wallet,
    walletLoaded,
    deployedToken,
    holder,
    freezer,
    bundleBuyWallets,
    setDeployedToken,
    setDeployedTokenName,
    setDeployedTokenSymbol,
    logMessage,
}: {
    connection: Connection;
    wallet: Keypair;
    walletLoaded: boolean;
    deployedToken: string;
    holder: Keypair;
    freezer: Keypair;
    bundleBuyWallets: Keypair[];
    setDeployedToken: React.Dispatch<React.SetStateAction<string>>;
    setDeployedTokenName: React.Dispatch<React.SetStateAction<string>>;
    setDeployedTokenSymbol: React.Dispatch<React.SetStateAction<string>>;
    logMessage: (message: string, type?: 'success' | 'error') => void;
}) {
    const {
        getSigners,
        getUpdateMetadataInstructions,
        getCreateTokenAccountInstructions,
        getCreateMintInstructions,
        getMintToInstructions,
        setAuthority,
        createTokenAccountsWithJito,
    } = useSplToken();
    const { spamAndConfirmBundle } = useJito();
    const { parseToken } = useBackend();
    const { isCopied, copyToClipboard } = useClipboard();

    const [tokenMintInput, setTokenMintInput] = useState<string>('');

    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [customImageUrl, setCustomImageUrl] = useState<string | undefined>('');

    const [tokenName, setTokenName] = useState<string>('');
    const [tokenDescription, setTokenDescription] = useState<string | undefined>(undefined);
    const [tokenSymbol, setTokenSymbol] = useState<string>('');
    const [tokenSupply, setTokenSupply] = useState<number>(1000000000);
    const [decimals, setDecimals] = useState<number>(6);
    const [twitterLink, setTwitterLink] = useState<string | undefined>(undefined);
    const [telegramLink, setTelegramLink] = useState<string | undefined>(undefined);
    const [websiteLink, setWebsiteLink] = useState<string | undefined>(undefined);

    const [mutableMetadata, setMutableMetadata] = useState<boolean>(false);
    const [transferOptions, setTransferOptions] = useState<boolean>(holder ? true : false);
    const [transferPercent, setTransferPercent] = useState<number>(CreateTokenTransferToHolderDefaultPercentage);
    const [createWithMintAuthorityRevoked, setCreateWithMintAuthorityRevoked] = useState<boolean>(true);
    const [createWithFreezeAuthorityRevoked, setCreateWithFreezeAuthorityRevoked] = useState<boolean>(true);
    const [tokenReceiver, setTokenReceiver] = useState<string>('');
    const [tokenReceiverAmount, setTokenReceiverAmount] = useState<number>(0);

    const [customTokenSecretKey, setCustomTokenSecretKey] = useState<string>('');

    const [usePumpSuffix, setUsePumpSuffix] = useState<boolean>(true);
    const [createATAAfterDeployment, setCreateATAAfterDeployment] = useState<boolean>(false);

    useEffect(() => {
        if (holder) {
            setTransferOptions(true);
        }
    }, [holder]);

    const tokenMintInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let link = event.target.value;

        if (link.includes('pump.fun')) {
            try {
                const url = new URL(link);
                const path = url.pathname.split('/').pop();
                setTokenMintInput(path || '');
            } catch (error) {
                console.error('Invalid URL format:', error);
                setTokenMintInput(link);
            }
        } else {
            setTokenMintInput(link);
        }
    };

    const tokenNameChange = (event: { target: { value: string } }) => {
        setTokenName(event.target.value);
    };

    const tokenDescriptionChange = (event: { target: { value: string } }) => {
        setTokenDescription(event.target.value);
    };

    const tokenSymbolChange = (event: { target: { value: string } }) => {
        setTokenSymbol(event.target.value);
    };

    const tokenSupplyChange = (event: { target: { value: string } }) => {
        setTokenSupply(Number(event.target.value));
    };

    const decimalsChange = (event: { target: { value: string } }) => {
        setDecimals(Number(event.target.value));
    };

    const mutableMetadataChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setMutableMetadata(checked);
    };

    const transferOptionsChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setTransferOptions(checked);
    };

    const transferPercentChange = (event: { target: { value: string } }) => {
        let value = Number(event.target.value);
        if (value > 100) {
            value = 100;
        }
        setTransferPercent(value);
    };

    const createWithMintAuthorityRevokedChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setCreateWithMintAuthorityRevoked(checked);
    };

    const createWithFreezeAuthorityRevokedChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setCreateWithFreezeAuthorityRevoked(checked);
    };

    const tokenReceiverChange = (event: { target: { value: string } }) => {
        setTokenReceiver(event.target.value);
    };

    const tokenReceiverAmountChange = (event: { target: { value: string } }) => {
        setTokenReceiverAmount(Number(event.target.value));
    };

    const customTokenSecretKeyChange = (event: { target: { value: string } }) => {
        setCustomTokenSecretKey(event.target.value);
    };

    const imagePreviewChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);

            setImageFile(file);

            const fileNameLabel = document.querySelector('.file-input-label span');
            if (fileNameLabel) {
                const allowedCharacterLength = 40;
                const fileName = file.name;

                fileNameLabel.textContent = fileName.length > allowedCharacterLength ? fileName.substring(0, allowedCharacterLength) + '...' : fileName;
            }
        }
    };

    const copyMintPublicKey = () => {
        copyToClipboard(deployedToken);
    };

    async function parseTokenInfo() {
        try {
            const parsedTokenMetadata = await parseToken(tokenMintInput);

            const name = parsedTokenMetadata.name;
            const symbol = parsedTokenMetadata.symbol;
            const imageUrl = parsedTokenMetadata.image;

            const description = parsedTokenMetadata.description || '';
            const telegram = parsedTokenMetadata.telegram || '';
            const twitter = parsedTokenMetadata.twitter || '';
            const website = parsedTokenMetadata.website || '';

            setTokenName(name);
            setTokenSymbol(symbol);
            setCustomImageUrl(imageUrl);
            setImagePreview(imageUrl || '');
            setTokenDescription(description);
            setTwitterLink(twitter);
            setTelegramLink(telegram);
            setWebsiteLink(website);
        } catch (error) {
            console.error('parseTokenInfo: ' + error);
            logMessage('Failed to parse token info: ' + (error as Error).message, 'error');
        }
    }

    async function uploadImageToIpfs() {
        const formData = new FormData();

        formData.append('file', imageFile);

        const metadata = JSON.stringify({
            name: 'File name',
        });
        formData.append('pinataMetadata', metadata);

        const options = JSON.stringify({
            cidVersion: 0,
        });
        formData.append('pinataOptions', options);

        const responseData = await axios.post('https://api.pinata.cloud/pinning/pinFileToIPFS', formData, {
            maxBodyLength: Infinity,
            headers: {
                'Content-Type': 'multipart/form-data',
                Authorization: `Bearer ${process.env.REACT_APP_PINATA_JWT}`,
            },
        });

        const fileUrl = 'https://gateway.pinata.cloud/ipfs/' + responseData.data.IpfsHash;

        return fileUrl;
    }

    async function uploadMetadataToIpfs(
        name: string,
        symbol: string,
        image: string,
        description?: string,
        twitter?: string,
        telegram?: string,
        website?: string,
    ) {
        const tokenMetadata: Record<string, string> = {
            name: name,
            symbol: symbol,
            ...(description && { description }),
            image: image,
            ...(twitter && { twitter }),
            ...(telegram && { telegram }),
            ...(website && { website }),
        };

        const uploadedData = await axios.post('https://api.pinata.cloud/pinning/pinJSONToIPFS', tokenMetadata, {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_PINATA_JWT}`,
            },
        });
        const metadataUrl = 'https://gateway.pinata.cloud/ipfs/' + uploadedData.data.IpfsHash;
        console.log('metadataUrl', metadataUrl);
        return metadataUrl;
    }

    async function deployMint(
        connection: Connection,
        payer: Keypair,
        mintAuthority: PublicKey,
        freezeAuthority: PublicKey,
        mintAmount: number,
        decimals: number,
        tokenName: string,
        symbol: string,
        description: string | undefined,
        mutableMetadata: boolean,
        customTokenSecretKey: string,
        usePumpSuffix: boolean,
    ) {
        try {
            if (!walletLoaded) return logMessage('Wallet not loaded');

            logMessage('Deploying token...');

            const imageUrl = customImageUrl ? customImageUrl : await uploadImageToIpfs();
            console.log('imageUrl', imageUrl);
            if (imageUrl === undefined) return logMessage('Image upload failed');
            const metadataUrl = await uploadMetadataToIpfs(tokenName, symbol, imageUrl, description, twitterLink, telegramLink, websiteLink);
            if (metadataUrl === undefined) return logMessage('Metadata upload failed');
            logMessage(`Metadata uploaded to IPFS on this link: ${metadataUrl}`);

            const instructions: TransactionInstruction[] = [];

            const customKeypair = usePumpSuffix
                ? await pvmb.getVanity()
                : customTokenSecretKey
                  ? Keypair.fromSecretKey(
                        customTokenSecretKey.includes('[') ? Uint8Array.from(JSON.parse(customTokenSecretKey)) : bs58.decode(customTokenSecretKey),
                    )
                  : undefined;
            const [mintInstructions, mint] = await getCreateMintInstructions(connection, payer, mintAuthority, freezeAuthority, decimals, customKeypair);
            instructions.push(...mintInstructions);

            const metadataInstructions = await getUpdateMetadataInstructions(payer, mint.publicKey, tokenName, symbol, metadataUrl, mutableMetadata);
            instructions.push(metadataInstructions);

            const createTokenAccountInstructions = getCreateTokenAccountInstructions(payer, mint.publicKey, payer.publicKey);
            instructions.push(createTokenAccountInstructions);
            const tokenAccount = createTokenAccountInstructions.keys[1].pubkey;

            const mintToInstructions = await getMintToInstructions(
                mint.publicKey,
                tokenAccount,
                mintAuthority,
                BigInt(mintAmount) * BigInt(Math.pow(10, decimals)),
            );
            instructions.push(mintToInstructions);
            if (transferOptions) {
                const holderAssociatedToken = getAssociatedTokenAddressSync(mint.publicKey, holder.publicKey);

                const transferAmount = (BigInt(mintAmount) * BigInt(Math.pow(10, decimals)) * BigInt(transferPercent * 100)) / BigInt(10000);
                const createInputTokenAccountInstructions = createAssociatedTokenAccountInstruction(
                    wallet.publicKey,
                    holderAssociatedToken,
                    holder.publicKey,
                    mint.publicKey,
                    TOKEN_PROGRAM_ID,
                    ASSOCIATED_TOKEN_PROGRAM_ID,
                );
                instructions.push(createInputTokenAccountInstructions);

                const holderAccount = createInputTokenAccountInstructions.keys[1].pubkey;
                const transferToInstructions = createTransferInstruction(tokenAccount, holderAccount, wallet.publicKey, transferAmount);
                instructions.push(transferToInstructions);
            }

            if (createWithMintAuthorityRevoked) {
                const [currentAuthorityPublicKey] = getSigners(wallet.publicKey, []);
                const setMintAuthInstructions = createSetAuthorityInstruction(
                    mint.publicKey,
                    currentAuthorityPublicKey,
                    AuthorityType.MintTokens,
                    null,
                    [],
                    TOKEN_PROGRAM_ID,
                );
                instructions.push(setMintAuthInstructions);
            }

            if (createWithFreezeAuthorityRevoked) {
                const [currentAuthorityPublicKey] = getSigners(wallet.publicKey, []);
                const setFreezeAuthInstructions = createSetAuthorityInstruction(
                    mint.publicKey,
                    currentAuthorityPublicKey,
                    AuthorityType.FreezeAccount,
                    null,
                    [],
                    TOKEN_PROGRAM_ID,
                );
                instructions.push(setFreezeAuthInstructions);
            } else if (freezer !== undefined && freezer.publicKey.toBase58() !== wallet.publicKey.toBase58()) {
                logMessage('Transfering freeze authority...');
                //transfer freeze authority to freezer
                logMessage(`New freezer account: ${freezer.publicKey.toBase58()}`);
                const [currentAuthorityPublicKey] = getSigners(wallet.publicKey, []);
                const transferFreezeAuthInstructions = createSetAuthorityInstruction(
                    mint.publicKey,
                    currentAuthorityPublicKey,
                    AuthorityType.FreezeAccount,
                    freezer.publicKey,
                    [],
                    TOKEN_PROGRAM_ID,
                );
                instructions.push(transferFreezeAuthInstructions);
            }

            const priorityFeeInstruction = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: createTokenPriorityFee });
            instructions.push(priorityFeeInstruction);

            const { lastValidBlockHeight, blockhash } = await connection.getLatestBlockhash();
            const deployMintMessage = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: blockhash,
                instructions: instructions,
            }).compileToV0Message();

            const deployTransaction = new VersionedTransaction(deployMintMessage);
            deployTransaction.sign([wallet, mint]);
            console.log('length', deployTransaction.serialize().length);

            // const deployMintBundleId = await sendBundle(connection, [deployTransaction], wallet, jitoTip);
            // logMessage(`Bundle id: ${deployMintBundleId}`);
            // const signature = encode(deployTransaction.signatures[0]);
            // logMessage(`Transaction signature: ${signature}`);
            // const signature = await connection.sendTransaction(deployTransaction, { skipPreflight: true });
            // logMessage(`Transaction sent: ${signature}`);

            await connection.sendTransaction(deployTransaction, { skipPreflight: true });

            await connection.confirmTransaction(
                { blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: bs58.encode(deployTransaction.signatures[0]) },
                'confirmed',
            );

            setDeployedToken(mint.publicKey.toBase58());
            setDeployedTokenName(tokenName);
            setDeployedTokenSymbol(symbol);
            logMessage(`Token has been minted: ${mint.publicKey.toBase58()}`);
            logMessage(`The token has been successfully deployed. The market can now be created.`, 'success');

            if (createATAAfterDeployment) {
                await createATAForBundleBuyers(mint.publicKey.toBase58());
            }
        } catch (error) {
            //logMessage(`Failed to deploy mint. ${error}`, 'error');
            console.error('Deploy mint: ' + error);
            //throw error;
        }
    }

    async function revokeMintAuthority(tokenPublicKey: string) {
        try {
            if (tokenPublicKey === '') {
                logMessage('Token not deployed!');
                return;
            }
            if (walletLoaded === false) {
                logMessage('WALLET IS NOT LOADED!');
                return;
            }

            logMessage('Revoking mint authority...');

            const signature = await setAuthority(
                connection,
                wallet,
                new PublicKey(tokenPublicKey),
                wallet.publicKey,
                AuthorityType.MintTokens,
                null,
                undefined,
                createTokenJitoTip,
            );
            logMessage('Revoked mint authority : ' + signature);
        } catch (error) {
            logMessage('Failed to revoke Mint: ' + (error as Error).message);
        }
    }

    async function revokeFreezeAuthority(wallet: Keypair, tokenPublicKey: string) {
        try {
            if (tokenPublicKey === '') {
                logMessage('Token not deployed!');
                return;
            }

            if (walletLoaded === false) {
                logMessage('WALLET IS NOT LOADED!');
                return;
            }

            logMessage('Revoking freeze authority...');

            const signature = await setAuthority(
                connection,
                wallet,
                new PublicKey(tokenPublicKey),
                wallet.publicKey,
                AuthorityType.FreezeAccount,
                null,
                undefined,
                createTokenJitoTip,
            );
            logMessage('Revoked freeze authority : ' + signature);
        } catch (error) {
            logMessage('Failed to revoke Freeze: ' + (error as Error).message);
        }
    }

    async function createATAForBundleBuyers(mint: string) {
        logMessage('Creating ATA for bundle buyers...');

        const createInAccountsPromise = createTokenAccountsWithJito(
            connection,
            bundleBuyWallets,
            new PublicKey(WSOL.mint),
            'processed',
            createTokenJitoTip,
            logMessage,
        );
        const createOutAccountsPromise = createTokenAccountsWithJito(
            connection,
            bundleBuyWallets,
            new PublicKey(mint),
            'processed',
            createTokenJitoTip,
            logMessage,
        );
        await Promise.all([createInAccountsPromise, createOutAccountsPromise]);

        logMessage('Created ATAs for bundle buyers', 'success');
    }

    return (
        <section>
            <div className='parse-token-info-container'>
                <input type='text' placeholder='Enter a Token Mint Address or pump.fun URL' value={tokenMintInput} onChange={tokenMintInputChange} />
                <button onClick={parseTokenInfo}>Parse Token Info</button>
            </div>
            <div className='parse-token-info-container'>
                <input
                    type='text'
                    placeholder='SecretKey For a Custom Token Address (optional)'
                    value={customTokenSecretKey}
                    onChange={customTokenSecretKeyChange}
                    disabled={usePumpSuffix}
                ></input>
                <label>Use Pump Suffix </label>
                <input type='checkbox' checked={usePumpSuffix} onChange={() => setUsePumpSuffix(!usePumpSuffix)} />
            </div>
            <section className='create-token-container'>
                <fieldset className='create-token-details'>
                    <legend>Token Details</legend>

                    <p>Image*</p>
                    <input type='file' id='imageInput' accept='image/*' onChange={imagePreviewChange} disabled={!walletLoaded} />
                    <label className='file-input-label' htmlFor='imageInput'>
                        🖼️ <span>Upload an image</span>
                    </label>

                    <label htmlFor='nameInput'>Name*</label>
                    <input type='text' id='nameInput' placeholder='Token name' value={tokenName} onChange={tokenNameChange} disabled={!walletLoaded} />

                    <label htmlFor='symbolInput'>Symbol*</label>
                    <input type='text' id='symbolInput' placeholder='Token symbol' value={tokenSymbol} onChange={tokenSymbolChange} disabled={!walletLoaded} />

                    <p>Description</p>
                    <textarea placeholder='Token description' value={tokenDescription} onChange={tokenDescriptionChange} disabled={!walletLoaded} />

                    <label htmlFor='supplyInput'>Supply*</label>
                    <input type='number' id='supplyInput' value={tokenSupply} onChange={tokenSupplyChange} disabled={!walletLoaded} />

                    <label htmlFor='decimalsInput'>Decimals*</label>
                    <input type='number' id='decimalsInput' value={decimals} onChange={decimalsChange} disabled={!walletLoaded} />

                    <label htmlFor='twitterInput'>Twitter Link</label>
                    <input
                        type='text'
                        id='twitterInput'
                        value={twitterLink}
                        onChange={(event) => {
                            setTwitterLink(event.target.value);
                        }}
                        disabled={!walletLoaded}
                    />

                    <label htmlFor='telegramInput'>Telegram Link</label>
                    <input
                        type='text'
                        id='telegramInput'
                        value={telegramLink}
                        onChange={(event) => {
                            setTelegramLink(event.target.value);
                        }}
                        disabled={!walletLoaded}
                    />

                    <label htmlFor='websiteInput'>Website Link</label>
                    <input
                        type='text'
                        id='websiteInput'
                        value={websiteLink}
                        onChange={(event) => {
                            setWebsiteLink(event.target.value);
                        }}
                        disabled={!walletLoaded}
                    />

                    <div className='create-token-mutable-metadata'>
                        <input type='checkbox' checked={mutableMetadata} onChange={mutableMetadataChange} disabled={!walletLoaded} />
                        <p>Changeable Metadata</p>
                    </div>
                </fieldset>

                <div className='preview-btn-container'>
                    <fieldset className='create-token-preview'>
                        <legend>Token Preview</legend>
                        <div className='create-token-preview--center'>
                            {imagePreview ? (
                                <div>
                                    <img src={imagePreview} alt='Token Preview' className='img-preview' />
                                </div>
                            ) : (
                                <div>
                                    <img src='https://placehold.co/180x180/000000/FFF' alt='Token Preview' className='img-preview' />
                                </div>
                            )}
                            <div className='token-info-container'>
                                <p className='token-info-headline'>Name:</p>
                                <p className='token-info-desciption'>{tokenName || '-'}</p>

                                <p className='token-info-headline'>Description:</p>
                                <p className='token-info-desciption'>{tokenDescription || '-'}</p>

                                <p className='token-info-headline'>Symbol:</p>
                                <p className='token-info-desciption'>{tokenSymbol || '-'}</p>

                                <p className='token-info-headline'>Supply:</p>
                                <p className='token-info-desciption'>{tokenSupply.toLocaleString() || '-'}</p>

                                <p className='token-info-headline'>Decimals:</p>
                                <p className='token-info-desciption'>{decimals || '-'}</p>

                                {deployedToken !== '' ? (
                                    <div className='create-token-preview-mint'>
                                        <p className='token-info-headline'>Mint:</p>
                                        <span>{deployedToken}</span>
                                        <button className={isCopied ? 'copied-button' : 'copy-button'} onClick={copyMintPublicKey}></button>
                                    </div>
                                ) : (
                                    <div className='create-token-preview-mint-empty-placeholder'></div>
                                )}
                            </div>

                            <button
                                className='create-token-preview-button'
                                onClick={() => {
                                    deployMint(
                                        connection,
                                        wallet,
                                        wallet.publicKey,
                                        wallet.publicKey,
                                        tokenSupply,
                                        decimals,
                                        tokenName,
                                        tokenSymbol,
                                        tokenDescription,
                                        mutableMetadata,
                                        customTokenSecretKey,
                                        usePumpSuffix,
                                    );
                                }}
                                disabled={!walletLoaded || !tokenName || !tokenSymbol || (!imageFile && !customImageUrl)}
                            >
                                Create Token
                            </button>
                        </div>
                    </fieldset>
                    <div className='buttons-container'>
                        <div className='token-creation-options'>
                            <div className='transfer-set'>
                                <label className='token-creation-label'>
                                    <input type='checkbox' checked={transferOptions} onChange={transferOptionsChange} disabled={!holder} />
                                    <span>Transfer % Supply To HOLDER Wallet</span>
                                </label>
                                <label className='token-creation-label token-creation-label-absolute percent-naming'>
                                    <input
                                        className='token-creation-percent-input'
                                        min={0}
                                        max={100}
                                        type='number'
                                        value={transferPercent}
                                        onChange={transferPercentChange}
                                        disabled={!holder}
                                    />
                                    <span>Percent</span>
                                    <button
                                        onClick={() => {
                                            setTransferPercent(79.31);
                                        }}
                                    >
                                        SET 79.31%
                                    </button>
                                </label>
                            </div>
                        </div>
                        <div className='token-creation-options'>
                            <label className='token-creation-label'>
                                <input
                                    type='checkbox'
                                    checked={createWithMintAuthorityRevoked}
                                    onChange={createWithMintAuthorityRevokedChange}
                                    disabled={!walletLoaded}
                                />
                                <span>Create With Mint Authority Revoked</span>
                            </label>
                        </div>
                        <div className='token-creation-options'>
                            <label className='token-creation-label'>
                                <input
                                    type='checkbox'
                                    checked={createWithFreezeAuthorityRevoked}
                                    onChange={createWithFreezeAuthorityRevokedChange}
                                    disabled={!walletLoaded}
                                />
                                <span>Create With Freeze Authority Revoked</span>
                            </label>
                        </div>
                        <div className='token-creation-options'>
                            <label className='token-creation-label'>
                                <input
                                    type='checkbox'
                                    checked={createATAAfterDeployment}
                                    onChange={() => setCreateATAAfterDeployment(!createATAAfterDeployment)}
                                    disabled={!walletLoaded}
                                />
                                <span>Open ATA for Bundle Buyers After Deployment</span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            <section className='remove-authority'>
                <button onClick={() => revokeMintAuthority(deployedToken)} disabled={!deployedToken}>
                    Revoke Mint{' '}
                </button>
                <button onClick={() => revokeFreezeAuthority(freezer, deployedToken)} disabled={!deployedToken}>
                    Revoke Freeze
                </button>
            </section>
            <hr />
            <section className='token-receiver'>
                <input type='text' value={tokenReceiver} onChange={tokenReceiverChange} placeholder='Receiver' disabled={!deployedToken} />
                <input type='number' value={tokenReceiverAmount} onChange={tokenReceiverAmountChange} disabled={!deployedToken} />
                <button disabled={!deployedToken}>MINT</button>
            </section>
            <hr className='divider' />
        </section>
    );
}
